import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface ListboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ListboxProps {
  id: string;
  value: string;
  options: readonly ListboxOption[];
  onChange: (value: string) => void;
  /** Shown while nothing is chosen; also selectable as the empty value. */
  placeholder?: string;
  /** Values that act on the list instead of closing it (e.g. "More countries…"). */
  stayOpenFor?: readonly string[];
  className?: string;
  'aria-label'?: string;
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  disabled?: boolean;
}

const TYPEAHEAD_WINDOW_MS = 600;
const MENU_ROOM_PX = 260;

/**
 * The widget's select: a button and a listbox in the brand's own type and colours instead of
 * the browser's popup, keyboard-complete (arrows, Home/End, Enter/Space, Escape, type-ahead),
 * focus staying on the button with `aria-activedescendant` naming the highlighted option.
 */
export function Listbox({
  id,
  value,
  options,
  onChange,
  placeholder,
  stayOpenFor = [],
  className = '',
  disabled = false,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: ListboxProps) {
  const list: readonly ListboxOption[] =
    placeholder !== undefined ? [{ value: '', label: placeholder }, ...options] : options;
  const listId = `${id}-listbox-${useId()}`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [upwards, setUpwards] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const selectedIndex = list.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? list[selectedIndex] : undefined;
  const enabled = (index: number) => Boolean(list[index] && !list[index]!.disabled);
  const step = (from: number, direction: 1 | -1): number => {
    let index = from;
    for (let n = 0; n < list.length; n++) {
      index = (index + direction + list.length) % list.length;
      if (enabled(index)) return index;
    }
    return from;
  };

  function openMenu() {
    if (disabled || list.length === 0) return;
    setActive(selectedIndex >= 0 ? selectedIndex : step(-1, 1));
    const rect = button.current?.getBoundingClientRect();
    setUpwards(
      Boolean(
        rect &&
        window.innerHeight - rect.bottom < MENU_ROOM_PX &&
        rect.top > window.innerHeight - rect.bottom
      )
    );
    setOpen(true);
  }
  function close() {
    setOpen(false);
    setActive(-1);
  }
  function choose(index: number) {
    const option = list[index];
    if (!option || option.disabled) return;
    if (stayOpenFor.includes(option.value)) {
      onChange(option.value);
      return;
    }
    close();
    if (option.value !== value) onChange(option.value);
  }

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || active < 0) return;
    menu.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) setActive((current) => step(current, 1));
        else openMenu();
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (open) setActive((current) => step(current, -1));
        else openMenu();
        return;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActive(step(-1, 1));
        }
        return;
      case 'End':
        if (open) {
          event.preventDefault();
          setActive(step(list.length, -1));
        }
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) choose(active);
        else openMenu();
        return;
      case 'Escape':
        if (open) {
          event.preventDefault();
          close();
        }
        return;
      case 'Tab':
        if (open) close();
        return;
      default:
        break;
    }
    if (event.key.length !== 1 || event.altKey || event.ctrlKey || event.metaKey) return;
    const now = Date.now();
    const text =
      (now - typed.current.at < TYPEAHEAD_WINDOW_MS ? typed.current.text : '') +
      event.key.toLowerCase();
    typed.current = { text, at: now };
    const from = open ? active : selectedIndex;
    for (let n = 1; n <= list.length; n++) {
      const index = (from + n + list.length) % list.length;
      if (enabled(index) && list[index]!.label.toLowerCase().startsWith(text)) {
        if (open) setActive(index);
        else choose(index);
        return;
      }
    }
  }

  return (
    <span ref={wrap} className={`zw-select-wrap ${className}`}>
      <button
        ref={button}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${id}-option-${active}` : undefined}
        aria-label={ariaLabel}
        aria-required={ariaRequired || undefined}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        className="zw-input zw-select"
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={`zw-select-value${selected ? '' : ' zw-select-placeholder'}`}>
          {selected?.label ?? placeholder ?? ''}
        </span>
      </button>
      {open ? (
        <ul
          ref={menu}
          id={listId}
          role="listbox"
          className={`zw-menu${upwards ? ' zw-menu--up' : ''}`}
        >
          {list.map((option, index) => (
            <li
              key={`${index}-${option.value}`}
              id={`${id}-option-${index}`}
              role="option"
              data-index={index}
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              className={`zw-option${index === active ? ' zw-option--active' : ''}`}
              onMouseMove={() => {
                if (index !== active && !option.disabled) setActive(index);
              }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
